import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// Hooks
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

// Selectors
import { getWorkspaceTitle } from 'Shared/selectors/workspace';

// Components
import { WpModal, WpModalHeader, WpModalBody } from 'Wpds/components/modal';
import { WpTabs, WpTab } from 'Wpds/components/tabs';
import WorkspaceDetailsMembers from './partials/WorkspaceDetailsMembers';
import WorkspaceDetailsSettings from './partials/WorkspaceDetailsSettings';

// Styles
import './WorkspaceDetailsModal.scss';

enum Tabs {
  settings = 'settings',
  members = 'members',
}

type Props = {
  isOpen: boolean;
  toggleIsOpen: () => void;
};

const WorkspaceDetailsModal = ({ isOpen, toggleIsOpen }: Props) => {
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(null);

  const { workspace } = useWorkspace();
  const workspaceTitle = useSelector(getWorkspaceTitle({ workspaceId: workspace.id }));

  const { isGroup } = workspace;

  const users = workspace?.users || [];

  useEffect(() => {
    switch (location.state?.view) {
      case 'members':
      case 'invite':
        setActiveTab(Tabs.members);
        break;
      default:
        setActiveTab(Tabs.settings);
        break;
    }
  }, [location]);

  const handleClose = () => {
    toggleIsOpen();
  };

  if (workspace.loading) return null;
  const baseClassName = 'WorkspaceDetailsModal';

  return (
    <WpModal isOpen={isOpen} onToggle={toggleIsOpen} size="sm">
      <WpModalHeader>
        {workspaceTitle}
        {isGroup && (
          <WpTabs activeTab={activeTab} onTabClick={setActiveTab}>
            <WpTab tabId={Tabs.settings}>Settings</WpTab>
            <WpTab tabId={Tabs.members}>Members</WpTab>
          </WpTabs>
        )}
      </WpModalHeader>
      <WpModalBody padding={0}>
        <div className={baseClassName}>
          {isGroup ? (
            <>
              {activeTab === Tabs.settings && (
                <WorkspaceDetailsSettings workspace={workspace} toggleSettingsModal={handleClose} />
              )}
              {activeTab === Tabs.members && (
                <WorkspaceDetailsMembers users={users} workspace={workspace} view={location.state?.view} />
              )}
            </>
          ) : (
            <WorkspaceDetailsSettings workspace={workspace} toggleSettingsModal={handleClose} />
          )}
        </div>
      </WpModalBody>
    </WpModal>
  );
};

export default WorkspaceDetailsModal;
